import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Check, Loader2, MessageSquareText } from "lucide-react";
import { Dialog, DialogDescription, DialogOverlay, DialogPortal, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { errorMessage, imgUrl, submitPhotoComment } from "@/lib/api";
import { useImageAlts } from "@/hooks/use-image-alts";
import { cn } from "@/lib/utils";

export const TEXT_MAX = 800;

/** «Nr. 17» – the picture's fixed number, quiet and monospaced. Renders nothing until the number is known.
 * `interactive` turns the chip into a button that opens the private-comment dialog – for photos that have no viewer of
 * their own (single figures, the film strip, the hero). Never use it inside a link or another button. */
export const PhotoNumberTag = ({ src, number, className, tone = "ink", interactive = false, testId }) => {
  const { numberFor } = useImageAlts();
  const [open, setOpen] = useState(false);
  const n = number ?? numberFor(src);
  if (!n) return null;
  const look = tone === "ink" ? "bg-ink/60 text-paper/90 backdrop-blur-sm" : tone === "paper" ? "bg-paper/85 text-ink backdrop-blur-sm" : "bg-secondary text-muted-foreground";
  const base = "inline-flex items-center rounded-[6px] px-1.5 py-0.5 font-mono text-[10.5px] font-medium tabular leading-none tracking-[0.04em]";
  if (!interactive) {
    return <span className={cn("pointer-events-none", base, look, className)} title={`Bilete nr. ${n}`} data-testid={testId || "photo-number"} data-number={n}>Nr. {n}</span>;
  }
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} aria-label={`Bilete nr. ${n} – kommenter privat til administrasjonen`} title={`Bilete nr. ${n} – kommenter`}
        className={cn(base, look, "gap-1.5 py-1 transition-[transform,background-color] duration-200 hover:scale-[1.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol", tone === "ink" && "hover:bg-ink/80", className)}
        data-testid={testId || "photo-number"} data-number={n} data-interactive="true">
        Nr. {n} <MessageSquareText className="h-3 w-3" aria-hidden="true" />
      </button>
      <PhotoCommentDialog src={src} number={n} open={open} onOpenChange={setOpen} testId={`${testId || "photo-number"}-comment`} />
    </>
  );
};

const EMPTY = { text: "", name: "", website: "" };

/**
 * A private note about one picture. The number is fixed when the dialog opens; if the picture under it changes (the viewer
 * steps on) the dialog closes and the draft is dropped, so a note can never land on the wrong photo. Nothing here is public.
 */
export const PhotoCommentDialog = ({ src, number, open, onOpenChange, testId = "photo-comment" }) => {
  const [form, setForm] = useState(EMPTY);
  const [sentId, setSentId] = useState(null);
  const [target, setTarget] = useState(null);   // {src, number} the dialog was opened for

  useEffect(() => {
    if (open) { setTarget({ src, number }); setForm(EMPTY); setSentId(null); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && target && (target.number !== number || target.src !== src)) onOpenChange(false);
  }, [open, target, number, src, onOpenChange]);

  const send = useMutation({
    mutationFn: () => submitPhotoComment(target.number, { text: form.text.trim(), name: form.name.trim() || undefined, page: window.location.pathname.slice(0, 200), website: form.website }),
    onSuccess: (r) => setSentId(r.id),
  });
  const text = form.text;
  const tooShort = text.trim().length < 2;
  const tooLong = text.length > TEXT_MAX;
  const canSend = !tooShort && !tooLong && !send.isPending && !sentId;
  const t = target || { src, number };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!send.isPending) onOpenChange(o); }}>
      {/* sits above the full-screen viewer (z-95), so overlay and content get their own layer */}
      <DialogPortal>
        <DialogOverlay className="z-[105]" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-[110] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-[16px] border border-foreground/20 bg-card p-0 text-foreground shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 focus:outline-none" data-testid={`${testId}-dialog`} onOpenAutoFocus={(e) => { e.preventDefault(); document.getElementById(`${testId}-text`)?.focus(); }} onKeyDown={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 border-b border-border p-4">
          {t.src && <img src={imgUrl(t.src, 240)} alt="" className="h-16 w-20 shrink-0 rounded-[8px] object-cover bg-secondary" data-testid={`${testId}-thumb`} />}
          <div className="min-w-0">
            <DialogTitle className="font-display text-[17px] font-semibold leading-snug">Kommenter bilete nr. {t.number}</DialogTitle>
            <DialogDescription className="mt-1 text-[13px] leading-snug text-muted-foreground" data-testid={`${testId}-privacy`}>
              Kommentaren vert sendt <strong className="font-medium text-foreground">privat til administrasjonen</strong> på Halsnøy Kulturhus. Han vert ikkje vist på nettsida.
            </DialogDescription>
          </div>
        </div>
        {sentId ? (
          <div className="p-5" data-testid={`${testId}-sent`}>
            <p className="flex items-start gap-2.5 text-[15px] leading-snug"><Check className="mt-0.5 h-5 w-5 shrink-0 text-status-confirmed" aria-hidden="true" /><span>Takk! Kommentaren om bilete nr. {t.number} er send privat til administrasjonen.</span></p>
            <div className="mt-5 flex justify-end"><Button type="button" className="h-10 rounded-[10px]" onClick={() => onOpenChange(false)} data-testid={`${testId}-close`}>Lukk</Button></div>
          </div>
        ) : (
          <form className="space-y-3 p-4" onSubmit={(e) => { e.preventDefault(); if (canSend) send.mutate(); }} noValidate>
            <div>
              <label htmlFor={`${testId}-text`} className="mb-1 block text-[13px] font-medium">Kommentar</label>
              <Textarea id={`${testId}-text`} value={text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} rows={4} maxLength={TEXT_MAX + 50} placeholder="T.d. «Dette er frå 17. mai 2019 – mannen til venstre er …»" className="min-h-[104px] bg-background text-[15px]" aria-invalid={tooLong || undefined} data-testid={`${testId}-text`} />
              <div className="mt-1 flex justify-between text-[12px] text-muted-foreground"><span>Ikkje skriv inn personopplysningar du ikkje treng dele.</span><span className={cn("tabular", tooLong && "text-destructive")} data-testid={`${testId}-count`}>{text.length}/{TEXT_MAX}</span></div>
            </div>
            <div>
              <label htmlFor={`${testId}-name`} className="mb-1 block text-[13px] font-medium">Namn <span className="font-normal text-muted-foreground">(valfritt)</span></label>
              <Input id={`${testId}-name`} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} maxLength={80} autoComplete="off" className="h-10 bg-background" data-testid={`${testId}-name`} />
            </div>
            {/* honeypot – off-screen for people, tempting for bots */}
            <div className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
              <label htmlFor={`${testId}-website`}>Nettside</label>
              <input id={`${testId}-website`} name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} data-testid={`${testId}-website`} />
            </div>
            {send.isError && <p className="text-[13px] text-destructive" role="alert" data-testid={`${testId}-error`}>{errorMessage(send.error, "Kunne ikkje sende kommentaren")}</p>}
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" className="h-10 rounded-[10px]" onClick={() => onOpenChange(false)} disabled={send.isPending} data-testid={`${testId}-cancel`}>Avbryt</Button>
              <Button type="submit" className="h-10 rounded-[10px]" disabled={!canSend} data-testid={`${testId}-submit`}>{send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquareText className="h-4 w-4" />} Send privat</Button>
            </div>
          </form>
        )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};
